import { useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useApi } from "./api/use-api";

export type UserRole = "guest" | "user" | "admin";

export interface ProtectedRouteOptions {
	requiredRole?: UserRole | UserRole[];
	requireVerified?: boolean;
	redirectTo?: string;
	fallback?: React.ReactNode;
}

export function useProtectedRoute(options: ProtectedRouteOptions = {}) {
	const navigate = useNavigate();
	const { auth, admins } = useApi();

	const { data: session, isPending: sessionLoading } = auth.useSession();
	const { data: adminCheck, isLoading: adminCheckLoading } =
		admins.checkCurrentUserIsAdmin({
			enabled: !!session?.user,
		});

	const isEmailVerified = !!session?.user?.emailVerified;

	const getUserRole = (): UserRole => {
		if (!session?.user) return "guest";
		if (adminCheck?.isAdmin === true) return "admin";
		return "user";
	};

	const checkAccess = (): boolean => {
		const currentRole = getUserRole();
		const { requiredRole, requireVerified } = options;

		if (!requiredRole) return true;

		const requiredRoles = Array.isArray(requiredRole)
			? requiredRole
			: [requiredRole];

		if (requiredRoles.includes("guest") && currentRole === "guest") return true;
		if (requiredRoles.includes("user") && currentRole === "user") {
			if (requireVerified && !isEmailVerified) return false;
			return true;
		}
		if (requiredRoles.includes("admin") && currentRole === "admin") return true;

		return false;
	};

	// Handle redirects for unauthorized access
	useEffect(() => {
		if (!sessionLoading && !session?.user) {
			const { redirectTo, requiredRole } = options;
			if (redirectTo && requiredRole) {
				const requiredRoles = Array.isArray(requiredRole)
					? requiredRole
					: [requiredRole];
				if (requiredRoles.includes("user") || requiredRoles.includes("admin")) {
					navigate({ to: redirectTo });
					return;
				}
			}
		}

		if (adminCheckLoading || sessionLoading) return;

		const currentRole = !session?.user
			? "guest"
			: adminCheck?.isAdmin === true
				? "admin"
				: "user";
		const { redirectTo, requiredRole } = options;

		let hasAccess = true;
		if (requiredRole) {
			const requiredRoles = Array.isArray(requiredRole)
				? requiredRole
				: [requiredRole];
			hasAccess = requiredRoles.some((role) => {
				if (role === "guest" && currentRole === "guest") return true;
				if (role === "user" && currentRole === "user") return true;
				if (role === "admin" && currentRole === "admin") return true;
				return false;
			});
		}

		if (!hasAccess && redirectTo) {
			// Prevent admins from accessing user-only routes
			if (currentRole === "admin" && requiredRole === "user") {
				navigate({ to: "/admin" });
				return;
			}

			// Prevent users from accessing admin-only routes (handled by route structure now)

			if (currentRole === "guest" && redirectTo === "/auth/signin") {
				navigate({ to: redirectTo });
			} else if (redirectTo) {
				navigate({ to: redirectTo });
			}
		}
	}, [
		adminCheckLoading,
		sessionLoading,
		session,
		adminCheck,
		navigate,
		options,
	]);

	const userRole = getUserRole();
	const hasAccess = checkAccess();

	return {
		isLoading: sessionLoading || (!!session?.user && adminCheckLoading),
		isAuthenticated: !!session?.user,
		isEmailVerified,
		userRole,
		hasAccess,
		user: session?.user,
		session,
	};
}
